import { combineReducers, createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  FilterSliceConfig,
  FiltersSliceState,
  FilterType,
  GroupFilterData,
  TagName,
} from "@shared/types";
import {
  TIME_RANGE_1_DAY,
  TIME_RANGE_1_WEEK,
  TIME_RANGE_START,
} from "@shared/common/constants";
import { get, isEqual } from "lodash";
import { createSelector } from "reselect";
import {
  getNumActiveFilters,
  loadFilters,
  loadGroupsFilter,
  selectAll,
  setFilterData,
} from "@shared/features/filters/utils";

export const createFiltersSlice = (config: FilterSliceConfig) => {
  const sliceName = "filters/" + config.baseSliceName;

  const initialState: FiltersSliceState = {
    config,
    rawData: undefined,
    timeRange: config.defaultTimeRange || TIME_RANGE_1_DAY,
    loadedFilters: false,
    loadedArgs: undefined,
    loadedFromQuery: config.loadedFromQuery ?? false,
    filters: {},
    mappings: {},
  };

  const slice = createSlice({
    name: sliceName,
    initialState,
    reducers: {
      loadGroups(
        state,
        action: PayloadAction<{
          data: GroupFilterData[];
        }>
      ) {
        loadGroupsFilter(state, action.payload.data);
      },
      setGroupsFilter(state, action) {
        setFilterData(state, FilterType.GROUPS, action.payload);
      },
      setMultiGroupsFilter(state, action) {
        setFilterData(state, FilterType.MULTI_GROUPS, action.payload);
      },
      setAmountType(state, action) {
        setFilterData(state, FilterType.AMOUNT_TYPE, action.payload);
      },
      setSourceFilter(state, action) {
        setFilterData(state, FilterType.SOURCE_NAME, action.payload);
      },
      setSignalStatusFilter(state, action) {
        setFilterData(state, FilterType.SIGNAL_STATUS, action.payload);
      },
      setFollowStatusFilter(state, action) {
        setFilterData(state, FilterType.FOLLOW_STATUS, action.payload);
      },
      setSourceTypeFilter(state, action) {
        setFilterData(state, FilterType.SOURCE_TYPE, action.payload);
      },
      setClientsFilter(state, action) {
        setFilterData(state, FilterType.CLIENTS, action.payload);
      },
      setExchangesFilter(state, action) {
        setFilterData(state, FilterType.EXCHANGES, action.payload);
      },
      setTimeFrameFilter(state, action) {
        setFilterData(state, FilterType.TIME_FRAME, action.payload);
      },
      setTimeRangeFilter(state, action) {
        state.timeRange = action.payload;
      },
      setSymbolsFilter(state, action) {
        setFilterData(state, FilterType.SYMBOLS, action.payload);
      },
      setPriceFilter(state, action) {
        setFilterData(state, FilterType.PRICE_RANGE, action.payload);
      },
      setIsHotFilter(state, action) {
        setFilterData(state, FilterType.IS_HOT, action.payload);
      },
      setUsdPriceRangeFilter(state, action) {
        setFilterData(state, FilterType.USD_PRICE_RANGE, action.payload);
      },
      setAvgRatingFilter(state, action) {
        setFilterData(state, FilterType.AVG_RATING, action.payload);
      },
      setAvgNumSignalsFilter(state, action) {
        setFilterData(state, FilterType.AVG_NUM_SIGNALS, action.payload);
      },
      setValidBotAmountFilter(state, action) {
        setFilterData(state, FilterType.VALID_BOT_AMOUNT, action.payload);
      },
      setConsecutiveActiveDaysFilter(state, action) {
        setFilterData(
          state,
          FilterType.CONSECUTIVE_ACTIVE_DAYS,
          action.payload
        );
      },
      setLeverageFilter(state, action) {
        setFilterData(state, FilterType.LEVERAGE, action.payload);
      },
      setLongShortFilter(state, action) {
        setFilterData(state, FilterType.LONG_SHORT, action.payload);
      },
      setStatusesFilter(state, action) {
        setFilterData(state, FilterType.STATUS, action.payload);
      },
      setMarginTypeFilter(state, action) {
        setFilterData(state, FilterType.MARGIN_TYPE, action.payload);
      },
      setTradeTypeFilter(state, action) {
        setFilterData(state, FilterType.TRADE_TYPE, action.payload);
      },
      setTagsFilter(state, action) {
        setFilterData(state, FilterType.TAGS, action.payload);
      },
      setBacktestingStatusFilter(state, action) {
        setFilterData(state, FilterType.BACKTESTING_STATUS, action.payload);
      },
      loadFilters(state, action) {
        const { data, args, onlyAddOptions, forceLoad } = action.payload || {};
        const filters = data?.filters;
        const configFiltersTypes = config.filterTypes;
        if (!configFiltersTypes || (!filters && !forceLoad)) {
          return;
        }
        const shouldLoadValues =
          !state.loadedFilters || !isEqual(args, state.loadedArgs) || forceLoad;
        loadFilters(
          state,
          configFiltersTypes,
          filters,
          true,
          shouldLoadValues,
          onlyAddOptions
        );
        state.loadedArgs = args;
        state.loadedFilters = true;
        state.rawData = filters;
      },
      updateFilters(
        state,
        action: PayloadAction<{
          filtersUpdateFunction?: (state: FiltersSliceState) => void;
        }>
      ) {
        action.payload?.filtersUpdateFunction?.(state);
      },
      selectAll(state, action: PayloadAction<FilterType[] | void>) {
        const filtersToLoad = action.payload ?? state.config.filterTypes;
        if (filtersToLoad?.length) {
          selectAll(state, filtersToLoad);
        }
      },
      setFiltersLoadedFromQuery(state) {
        state.loadedFromQuery = true;
      },
    },
  });

  const selectFilters = (state: any): FiltersSliceState => {
    return get(state, sliceName.split("/"));
  };
  const selectFiltersData = createSelector(
    selectFilters,
    (filters) => filters.filters
  );
  const selectFiltersConfig = createSelector(
    selectFilters,
    (filters) => filters.config
  );
  const selectTimeRangeFilter = createSelector(
    selectFilters,
    (filters) => filters.timeRange
  );
  const selectAmountTypeFilter = createSelector(
    selectFiltersData,
    (filters) => filters[FilterType.AMOUNT_TYPE]?.value?.[0]
  );
  const selectGroupsFilter = createSelector(
    selectFiltersData,
    (filters) => filters[FilterType.GROUPS]?.value || []
  );
  const selectMultiGroupsFilter = createSelector(
    selectFiltersData,
    (filters) => filters[FilterType.MULTI_GROUPS]?.value || []
  );
  const selectClientsFilter = createSelector(
    selectFiltersData,
    (filters) => filters[FilterType.CLIENTS]?.value || []
  );
  const selectExchangesFilter = createSelector(
    selectFiltersData,
    (filters) => filters[FilterType.EXCHANGES]?.value || []
  );
  const selectStatusFilter = createSelector(
    selectFiltersData,
    (filters) => filters[FilterType.STATUS]?.value || []
  );
  const selectExchangeToClientsMapping = createSelector(
    selectFilters,
    (filters) => filters.mappings.exchangeToClientsMapping || {}
  );
  const selectPriceRangeFilter = createSelector(
    selectFiltersData,
    (filters) => filters[FilterType.PRICE_RANGE]
  );
  const selectUsdPriceRangeFilter = createSelector(
    selectFiltersData,
    (filters) => filters[FilterType.USD_PRICE_RANGE]
  );
  const selectNumActiveFilters = createSelector(selectFilters, (filters) =>
    getNumActiveFilters(filters)
  );

  return {
    reducer: slice.reducer,
    actions: slice.actions,
    selectors: {
      selectFilters,
      selectFiltersData,
      selectFiltersConfig,
      selectTimeRangeFilter,
      selectGroupsFilter,
      selectMultiGroupsFilter,
      selectClientsFilter,
      selectExchangesFilter,
      selectExchangeToClientsMapping,
      selectPriceRangeFilter,
      selectUsdPriceRangeFilter,
      selectAmountTypeFilter,
      selectNumActiveFilters,
      selectStatusFilter,
    },
  };
};

export const accountExtraDetailsFilters = createFiltersSlice({
  baseSliceName: "accountExtraDetails",
});
export const tradeExtraDetailsFilters = createFiltersSlice({
  baseSliceName: "tradeExtraDetails",
  defaultTimeRange: TIME_RANGE_START,
});

const tradesFiltersTypes = [
  FilterType.TIME_FRAME,
  FilterType.EXCHANGES,
  FilterType.CLIENTS,
  FilterType.SOURCE_TYPE,
  FilterType.SOURCE_NAME,
  FilterType.LONG_SHORT,
  FilterType.MARGIN_TYPE,
  FilterType.LEVERAGE,
];
export const openTradesFilters = createFiltersSlice({
  baseSliceName: "openTrades",
  filterTypes: [FilterType.IS_HOT, ...tradesFiltersTypes],
  defaultTimeRange: TIME_RANGE_1_WEEK,
});
export const closedTradesFilters = createFiltersSlice({
  baseSliceName: "closedTrades",
  filterTypes: tradesFiltersTypes,
  defaultTimeRange: TIME_RANGE_1_WEEK,
});

export const detailsDrawerOpenTradesFilters = createFiltersSlice({
  baseSliceName: "detailsDrawerOpenTrades",
  filterTypes: tradesFiltersTypes,
  loadedFromQuery: true,
});
export const detailsDrawerClosedTradesFilters = createFiltersSlice({
  baseSliceName: "detailsDrawerClosedTrades",
  filterTypes: tradesFiltersTypes,
  loadedFromQuery: true,
});

export const createTradeFilters = createFiltersSlice({
  baseSliceName: "createTrade",
});

export const marketplaceFilters = createFiltersSlice({
  baseSliceName: "marketplace",
  filterTypes: [
    FilterType.PRICE_RANGE,
    FilterType.TAGS,
    FilterType.EXCHANGES,
    FilterType.AVG_RATING,
    FilterType.AVG_NUM_SIGNALS,
  ],
  configTags: [
    TagName.NEW,
    TagName.TOP_SELLER,
    TagName.SALE,
    TagName.FREE,
    TagName.FREE_TRIAL,
  ],
});

export const portfolioFilters = createFiltersSlice({
  baseSliceName: "portfolio",
  filterTypes: [
    FilterType.EXCHANGES,
    FilterType.CLIENTS,
    FilterType.AMOUNT_TYPE,
    FilterType.STATUS,
  ],
});

const signalsFilterTypes = [
  FilterType.GROUPS,
  FilterType.SIGNAL_STATUS,
  FilterType.FOLLOW_STATUS,
  FilterType.TIME_FRAME,
  FilterType.EXCHANGES,
  FilterType.LONG_SHORT,
  FilterType.MARGIN_TYPE,
  FilterType.LEVERAGE,
];
const closedSignalsFilterTypes = signalsFilterTypes.filter(
  (filterType) => filterType !== FilterType.SIGNAL_STATUS
);
export const adminClosedSignalsFilters = createFiltersSlice({
  baseSliceName: "adminClosedSignals",
  filterTypes: closedSignalsFilterTypes,
});
export const adminOpenSignalsFilters = createFiltersSlice({
  baseSliceName: "adminOpenSignals",
  filterTypes: signalsFilterTypes,
});
export const userClosedSignalsFilters = createFiltersSlice({
  baseSliceName: "userClosedSignals",
  filterTypes: closedSignalsFilterTypes,
});
export const userOpenSignalsFilters = createFiltersSlice({
  baseSliceName: "userOpenSignals",
  filterTypes: signalsFilterTypes,
});

export const publicBotsFilters = createFiltersSlice({
  baseSliceName: "publicBots",
  filterTypes: [
    FilterType.VALID_BOT_AMOUNT,
    FilterType.EXCHANGES,
    FilterType.SYMBOLS,
    FilterType.CONSECUTIVE_ACTIVE_DAYS,
    FilterType.LONG_SHORT,
  ],
});

const myBotsFiltersTypes = [
  FilterType.TIME_FRAME,
  FilterType.EXCHANGES,
  FilterType.CLIENTS,
  FilterType.SYMBOLS,
  FilterType.STATUS,
  FilterType.LONG_SHORT,
  FilterType.MARGIN_TYPE,
  FilterType.LEVERAGE,
];

export const myDCABotsFilters = createFiltersSlice({
  baseSliceName: "myDCABots",
  filterTypes: myBotsFiltersTypes,
});

export const myTradingViewBotsFilters = createFiltersSlice({
  baseSliceName: "myTradingViewBots",
  filterTypes: myBotsFiltersTypes,
});

const signalsBotsFiltersTypes = myBotsFiltersTypes.filter(
  (filterType) =>
    filterType !== FilterType.SYMBOLS && filterType !== FilterType.LEVERAGE
);
signalsBotsFiltersTypes.splice(1, 0, FilterType.MULTI_GROUPS);

export const mySignalsBotsFilters = createFiltersSlice({
  baseSliceName: "mySignalsBots",
  filterTypes: signalsBotsFiltersTypes,
});

export const myGridBotsFilters = createFiltersSlice({
  baseSliceName: "myGridBots",
  filterTypes: myBotsFiltersTypes,
});

export const groupConfigurationsFilters = createFiltersSlice({
  baseSliceName: "groupConfigurations",
  filterTypes: [FilterType.GROUPS, FilterType.TIME_FRAME, FilterType.STATUS],
});

export const dashboardFilters = createFiltersSlice({
  baseSliceName: "dashboard",
  filterTypes: [FilterType.EXCHANGES, FilterType.CLIENTS],
});

const backtestingFiltersTypes = [
  FilterType.TIME_FRAME,
  FilterType.SYMBOLS,
  FilterType.MULTI_GROUPS,
  FilterType.CLIENTS,
  FilterType.EXCHANGES,
  FilterType.BACKTESTING_STATUS,
];

export const signalsBotBacktestingFilters = createFiltersSlice({
  baseSliceName: "signalsBotBacktesting",
  filterTypes: backtestingFiltersTypes.filter(
    (filterType) => filterType !== FilterType.SYMBOLS
  ),
});

export const dcaBotBacktestingFilters = createFiltersSlice({
  baseSliceName: "dcaBotBacktesting",
  filterTypes: backtestingFiltersTypes.filter(
    (filterType) => filterType !== FilterType.MULTI_GROUPS
  ),
});

export const filtersReducer = combineReducers({
  accountExtraDetails: accountExtraDetailsFilters.reducer,
  userOpenSignals: userOpenSignalsFilters.reducer,
  userClosedSignals: userClosedSignalsFilters.reducer,
  openTrades: openTradesFilters.reducer,
  closedTrades: closedTradesFilters.reducer,
  tradeExtraDetails: tradeExtraDetailsFilters.reducer,
  createTrade: createTradeFilters.reducer,
  marketplace: marketplaceFilters.reducer,
  portfolio: portfolioFilters.reducer,
  adminOpenSignals: adminOpenSignalsFilters.reducer,
  adminClosedSignals: adminClosedSignalsFilters.reducer,
  publicBots: publicBotsFilters.reducer,
  myDCABots: myDCABotsFilters.reducer,
  myTradingViewBots: myTradingViewBotsFilters.reducer,
  mySignalsBots: mySignalsBotsFilters.reducer,
  myGridBots: myGridBotsFilters.reducer,
  groupConfigurations: groupConfigurationsFilters.reducer,
  detailsDrawerOpenTrades: detailsDrawerOpenTradesFilters.reducer,
  detailsDrawerClosedTrades: detailsDrawerClosedTradesFilters.reducer,
  dashboard: dashboardFilters.reducer,
  signalsBotBacktesting: signalsBotBacktestingFilters.reducer,
  dcaBotBacktesting: dcaBotBacktestingFilters.reducer,
});
